import {CommonModule, DOCUMENT} from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatTableModule} from '@angular/material/table';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {ResourceAttachmentsComponent} from '@app/resource/resource-attachments.component';
import {EventResponse, GenericResourceResponse, OrganizationResponse, ResourceService, UserResponse} from '@middleware';
import {ResourcePermissionsFlags} from '@models/search';
import {UserService} from '@services/user/user.service';
import {
  KeyValueTableComponent,
  keyValueTableObject,
} from '@shared/components/key-value-table/key-value-table.component';
import {
  SectionHeaderAction,
  SectionHeaderActions,
} from '@shared/components/section-header-actions/section-header-actions';
import {EventMetadataPipe} from '@shared/pipes/format-metadata/event-metadata.pipe';
import {firstValueFrom} from 'rxjs';

export type AnyResource = (GenericResourceResponse | EventResponse) & ResourcePermissionsFlags;
/**
 * BaseResource, BaseProject, BaseDataset -> has permissions
 *
 * */

@Component({
  selector: 'app-resource',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTableModule,
    KeyValueTableComponent,
    MatButtonModule,
    EventMetadataPipe,
    RouterLink,
    ResourceAttachmentsComponent,
  ],
  templateUrl: './resource.component.html',
  styleUrl: './resource.component.scss',
})
export class ResourceComponent implements OnInit, OnDestroy, AfterViewInit, SectionHeaderActions {
  private readonly route = inject(ActivatedRoute);
  private readonly resourceService = inject(ResourceService);
  private readonly userService = inject(UserService);
  private readonly document = inject(DOCUMENT);
  private scriptElement?: HTMLScriptElement;
  private readonly sanitizer = inject(DomSanitizer);

  public user: UserResponse;

  public readonly resource = signal<AnyResource | null>(null);
  public readonly isEventResource = computed(() => (this.resource() as EventResponse)?.event_metadata !== undefined);

  readonly event = computed(() =>
    this.isEventResource() ? (this.resource() as EventResponse & ResourcePermissionsFlags) : undefined,
  );

  constructor() {
    // Track the resource
    effect(() => {
      const data = this.route.snapshot.data['resource'] as AnyResource;
      if (data) this.resource.set(data);
    });

    // Track the user
    effect(() => {
      this.user = this.userService.userSignal();
    });
  }

  // AddEvent 1.6.1 (default branded mode, no API key). Script loaded locally.
  ngOnInit() {
    this.scriptElement = this.document.createElement('script');
    this.scriptElement.src = 'assets/js/atc.min.js';
    this.scriptElement.async = true;
    this.scriptElement.defer = true;
    this.document.body.appendChild(this.scriptElement);
  }

  ngOnDestroy() {
    if (this.scriptElement) {
      this.document.body.removeChild(this.scriptElement);
    }
  }

  // Manually remove the dynamically-generated calendar icon
  ngAfterViewInit() {
    setTimeout(() => {
      const icons = document.querySelectorAll('.addeventatc_icon.atc_node.notranslate');
      icons.forEach(icon => icon.remove());
    }, 100);
  }

  public readonly safeDescription = computed<SafeHtml>(() => {
    const description = this.resource()?.description;
    if (!description) return '';

    // Bypass Angular's sanitizer to allow this specific piece of HTML to be WYSIWYG
    return this.sanitizer.bypassSecurityTrustHtml(description); // nosonar
  });

  audienceResolver(orgs: OrganizationResponse[]): string {
    const resolvedAudiences: string[] = [...orgs]
      .filter(org => org.full_name !== 'Other/External')
      .map(org => org.full_name);

    if (orgs.some(org => org.full_name === 'Other/External')) {
      resolvedAudiences.push('Public');
    }

    return resolvedAudiences.join(', ');
  }

  private buildCategoryValue(): string[] {
    const resource = this.resource();
    if (!resource?.categories?.length) return [];

    return resource.categories.map(
      cat => `${cat.secondary_category.primary_category.name} > ${cat.secondary_category.name} > ${cat.name}`,
    );
  }

  private buildAddressValue(): string[] {
    const resource = this.resource();
    const spatialCoverage = resource?.spatial_coverage;
    if (!spatialCoverage) return [];

    // Build address parts as array, filtering out null/empty values
    const parts: string[] = [];

    if (spatialCoverage.street) {
      parts.push(spatialCoverage.street);
    }

    const cityState = [spatialCoverage.city, spatialCoverage.state].filter(val => val).join(', ');
    if (cityState && spatialCoverage.zip_code) {
      parts.push(`${cityState} ${spatialCoverage.zip_code}`);
    } else if (cityState) {
      parts.push(cityState);
    } else if (spatialCoverage.zip_code) {
      parts.push(spatialCoverage.zip_code);
    }

    if (spatialCoverage.country) {
      parts.push(spatialCoverage.country);
    }

    return parts;
  }

  public readonly resourceTableData = computed<keyValueTableObject>(() => {
    const resource = this.resource();
    if (!resource) return [];
    const tableData: keyValueTableObject = [];

    // Place the single Contact into a Contact[] array:
    if (resource.contacts) {
      const contacts = Array.isArray(resource.contacts) ? resource.contacts : [resource.contacts];

      const name = contacts[0].name ?? '';
      const email = contacts[0].email ?? '';
      const phone = contacts[0].phone_number ?? '';

      // Only push contact data if at least one field is populated
      if (name || email || phone) {
        const contactData = [name, email, phone];
        tableData.push({
          key: 'contacts',
          display_name: 'Contact',
          value: contactData,
        });
      }
    }

    tableData.push(
      {
        key: 'creator_organization',
        display_name: 'Creator Organization',
        value: resource.creator?.organization?.full_name || '',
      },
      {
        key: 'categories',
        display_name: 'Categories',
        value: this.buildCategoryValue(),
      },
      {
        key: 'address',
        display_name: 'Address',
        value: this.buildAddressValue(),
      },
    );

    if (this.normalizeURL() !== '') {
      tableData.push({
        key: 'website_url',
        display_name: 'Website',
        value: this.normalizeURL(),
        link: this.normalizeURL(),
      });
    }

    return tableData;
  });

  public sectionHeaderActions: WritableSignal<SectionHeaderAction[]> = signal([
    {
      target: '/help/resource-explorer/view-resource',
      targetTab: '_blank',
      caption: 'Help',
      color: 'var(--mat-sys-primary)',
      icon: 'help_outline',
    },
  ]);

  isFavoritedByUser = computed(() => {
    const r = this.resource();
    const u = this.user;
    if (!r || !u) return false;

    const favoritedUsers = Array.isArray(r.favorited_users)
      ? r.favorited_users.map(fu => (typeof fu === 'string' ? fu : fu?.email))
      : [];
    return favoritedUsers.includes(u.email);
  });

  /** Favorites: Update the signal first, then send an API call. */
  async addFavorite() {
    const r = this.resource();
    if (!r || !this.user) return;

    this.resource.update(curr =>
      curr
        ? {
            ...(curr as AnyResource),
            favorited_users: [
              ...(curr.favorited_users?.map(fu => (typeof fu === 'string' ? fu : fu.email)) ?? []),
              this.user.email,
            ],
          }
        : curr,
    );

    try {
      await firstValueFrom(this.resourceService.favoriteResource(r.id, {remove: false}));
    } catch (err) {
      this.resource.update(curr =>
        curr
          ? {
              ...(curr as AnyResource),
              favorited_users: (curr.favorited_users?.map(fu => (typeof fu === 'string' ? fu : fu.email)) ?? []).filter(
                e => e !== this.user.email,
              ),
            }
          : curr,
      );
    }
  }

  async removeFavorite() {
    const r = this.resource();
    if (!r || !this.user) return;

    this.resource.update(curr =>
      curr
        ? {
            ...(curr as AnyResource),
            favorited_users: (curr.favorited_users?.map(fu => (typeof fu === 'string' ? fu : fu.email)) ?? []).filter(
              e => e !== this.user.email,
            ),
          }
        : curr,
    );

    try {
      await firstValueFrom(this.resourceService.favoriteResource(r.id, {remove: true}));
    } catch (err) {
      this.resource.update(curr =>
        curr
          ? {
              ...(curr as AnyResource),
              favorited_users: [
                ...(curr.favorited_users?.map(fu => (typeof fu === 'string' ? fu : fu.email)) ?? []),
                this.user.email,
              ],
            }
          : curr,
      );
    }
  }
  async toggleFavorite() {
    if (!this.user) return;

    if (this.isFavoritedByUser()) {
      await this.removeFavorite();
    } else {
      await this.addFavorite();
    }
  }

  // If a URL was not saved with http(s), fix that:
  normalizeURL(): string {
    let url = this.resource()?.website_url || '';
    if (url.trim() === '') {
      return '';
    }
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }
    return url;
  }
}
